import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { useLang } from "../i18n";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import Header from "../components/Header";
import DelftTile from "../components/DelftTile";
import { Button } from "../components/ui/button";
import { ArrowRight, Sparkles, MessageCircle, Heart, Gem, Flame, BookOpen, Download } from "lucide-react";

const PHASE_ICONS = [MessageCircle, MessageCircle, Heart, Gem, Flame, BookOpen];

export default function Landing() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  useDocumentTitle(t("tagline"));
  const [heroTile, setHeroTile] = useState("");
  const dlRef = useRef(null);

  useEffect(() => {
    api.get("/landing-tiles/random").then(({ data }) => { if (data.text) setHeroTile(data.text); }).catch(() => {});
  }, []);

  const downloadTile = async () => {
    if (!dlRef.current) return;
    try {
      const dataUrl = await toPng(dlRef.current, { pixelRatio: 3, cacheBust: true });
      const a = document.createElement("a");
      a.download = "core-emotion.png";
      a.href = dataUrl;
      a.click();
    } catch (e) {
      toast.error(lang === "nl" ? "Download mislukt" : "Download failed");
    }
  };

  const phases = [1, 2, 3, 4, 5, 6].map((n) => ({
    n,
    title: t(`p${n}_title`),
    intro: t(`p${n}_intro`),
    Icon: PHASE_ICONS[n - 1],
  }));

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute left-1/4 top-40 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-2 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-secondary px-4 py-1.5 text-sm font-semibold text-primary">
              <Sparkles className="h-4 w-4" /> {t("tagline")}
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-primary sm:text-5xl lg:text-6xl">
              {t("hero_title")}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">{t("hero_sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-start-btn" size="lg" className="h-13 rounded-full px-7 text-base"
                onClick={() => nav(user ? "/journey" : "/auth")}>
                {t("get_started")} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="relative mx-auto w-full max-w-md">
            <div className="drop-shadow-2xl">
              <DelftTile line1={heroTile} testId="hero-tile" />
            </div>
            {heroTile && (
              <div className="mt-4 flex justify-center">
                <Button data-testid="landing-tile-download" variant="outline" className="rounded-full border-primary/40 text-primary"
                  onClick={downloadTile}>
                  <Download className="mr-2 h-4 w-4" /> {t("download")}
                </Button>
              </div>
            )}
            {/* hidden copy with watermark, used only for download */}
            <div aria-hidden className="pointer-events-none fixed -left-[9999px] top-0 w-[600px]">
              <DelftTile innerRef={dlRef} line1={heroTile} watermark testId="hero-tile-dl" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24">
        <h2 className="mb-10 text-center font-display text-3xl font-bold tracking-tight text-primary sm:text-4xl">
          {lang === "nl" ? "Zes fases naar jullie merkessentie" : "Six phases to your brand essence"}
        </h2>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {phases.map(({ n, title, intro, Icon }, i) => (
            <motion.div key={n}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="group rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10">
              <div className="mb-4 flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="text-sm font-bold uppercase tracking-widest text-accent">{t("phase")} {n}</span>
              </div>
              <h3 className="font-display text-xl font-semibold text-primary">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-4">{intro}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
